import { useParams, Navigate, Link } from "react-router-dom";
import { Layout } from "../components/Layout";
import { Seo } from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { Reveal, MaskedLines } from "../components/motion";
import { AffiliateButton, ScoreBadge } from "../components/Affiliate";
import { VpnLogo } from "../components/VpnLogo";
import { CtaBanner } from "../components/CtaBanner";
import { getVpn } from "../data/vpns";
import { getRanking, RANKINGS } from "../data/rankings";

const RankingPage = () => {
  const { slug } = useParams();
  const page = getRanking(slug);
  if (!page) return <Navigate to="/rankings" replace />;

  return (
    <Layout>
      <Seo title={page.metaTitle} description={page.metaDescription} />
      <section className="grain relative overflow-hidden bg-[#0F172A]">
        <div className="hero-grid absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-[#7EAAFF]">2026 Rankings</p>
          <h1 className="font-display text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
            <MaskedLines lines={[page.title]} />
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300">{page.intro}</p>
        </div>
      </section>

      <Breadcrumbs crumbs={[{ label: "Rankings", to: "/rankings" }, { label: page.label }]} />

      <section className="mx-auto max-w-4xl px-6 py-16 lg:px-8" data-testid="ranking-list">
        <div className="border border-slate-200">
          <div className="grid grid-cols-[auto_1fr_auto] items-center gap-4 border-b-2 border-slate-200 bg-slate-50 px-6 py-4 sm:grid-cols-[auto_auto_1fr_auto]">
            <span className="font-display text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Rank</span>
            <span className="hidden font-display text-xs font-bold uppercase tracking-[0.2em] text-slate-400 sm:block">Score</span>
            <span className="font-display text-xs font-bold uppercase tracking-[0.2em] text-slate-400">VPN · {page.metric}</span>
            <span />
          </div>
          {page.order.map((s, i) => {
            const v = getVpn(s);
            const top = i === 0;
            return (
              <Reveal key={s} delay={i * 0.04}>
                <div
                  data-testid={`ranking-row-${s}`}
                  className={`grid grid-cols-[auto_1fr_auto] items-center gap-4 border-b border-slate-100 px-6 py-5 sm:grid-cols-[auto_auto_1fr_auto] ${
                    top ? "bg-[#4687FF]/[0.06]" : "bg-white"
                  }`}
                >
                  <span className="font-display text-2xl font-black tracking-tighter text-slate-300 w-10">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="hidden sm:block">
                    <ScoreBadge score={v.score} size="sm" />
                  </span>
                  <div className="flex items-center gap-4">
                    <VpnLogo vpn={v} size={56} />
                    <div>
                      <Link to={`/reviews/${s}`} className="font-display text-lg font-extrabold tracking-tight text-[#0F172A] hover:text-[#4687FF]">
                        {v.name}
                      </Link>
                      <p className="text-sm text-slate-500">{page.values[s]}</p>
                    </div>
                  </div>
                  <AffiliateButton
                    href={v.link}
                    testId={`ranking-cta-${s}`}
                    variant={top ? "primary" : "outline"}
                    className="!px-4 !py-2 !text-xs"
                  >
                    Visit
                  </AffiliateButton>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 pb-20 lg:px-8">
        <Reveal className="border-t border-slate-200 pt-10">
          <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-slate-400">More Rankings</h3>
          <div className="mt-4 flex flex-wrap gap-3">
            {RANKINGS.filter((r) => r.slug !== slug).map((r) => (
              <Link
                key={r.slug}
                to={`/rankings/${r.slug}`}
                className="border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition-colors duration-150 hover:border-[#4687FF] hover:text-[#4687FF]"
              >
                {r.label}
              </Link>
            ))}
          </div>
        </Reveal>
      </section>
      <CtaBanner />
    </Layout>
  );
};

export default RankingPage;
